import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { fmtErr, API } from "../../lib/api";
import { Anchor, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import LanguageSwitcher from "../../components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/app";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Lightweight breadcrumb helper — surfaces invisible iPad failures through
  // the GlobalErrorOverlay's debug pill so we can see exactly how far the
  // submit got. No-ops if the overlay isn't mounted yet.
  const crumb = (msg) => {
    try { window.__seavantoDebug && window.__seavantoDebug.log("login: " + msg); } catch {}
  };

  const submit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    crumb(`submit fired (email_len=${email.length}, pw_len=${password.length})`);
    // Guard against double-submits (Enter key + button tap simultaneously on
    // iPad with external keyboard, or the onClick fallback firing on top of
    // the form's native submit).
    if (busy) { crumb("already busy, ignoring"); return; }
    if (!email || !password) {
      crumb("missing email or password");
      setErr(t("auth.wrong_credentials"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      try {
        crumb("calling login() against " + API);
        await login(email, password);
        crumb("login() resolved, navigating to " + from);
        nav(from, { replace: true });
      } catch (e2) {
        crumb("login() threw: " + (e2?.message || "unknown") + " status=" + (e2?.response?.status || "n/a"));
        // requires_verification → redirect to OTP entry.
        const detail = e2?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "requires_verification") {
          nav(`/verify-email?email=${encodeURIComponent(detail.email || email)}`, { replace: true });
          return;
        }
        // Network-level failure (no response object → axios reached us with
        // ERR_NETWORK / CORS / timeout). Tell the user *something* concrete
        // so they don't sit staring at a frozen-looking form. This was the
        // root cause of the "no error, no progress" report.
        if (!e2?.response) {
          const msg = e2?.message === "Network Error"
            ? t("auth.network_error")
            : t("auth.login_failed", { message: e2?.message || "unknown network error" });
          setErr(msg);
          return;
        }
        // Status-specific helpers — 401 is the most common (wrong creds), 429
        // is brute-force lockout (not enabled yet but planned), 5xx means the
        // server itself is sick.
        const status = e2.response.status;
        const detailStr = typeof detail === "string" ? detail : "";
        if (status === 401 && (!detailStr || detailStr.toLowerCase().includes("invalid"))) {
          setErr(t("auth.wrong_credentials"));
          return;
        }
        if (status >= 500) {
          setErr(t("auth.server_error", { status }));
          return;
        }
        setErr(fmtErr(e2));
      }
    } catch (synchronous) {
      // Outer catch — catches anything that escapes the inner try, including
      // a crash inside the error-handler itself (e.g. an i18n key missing in
      // an older bundle). Without this, the failure would bubble to
      // window.onerror but the button would still appear frozen.
      crumb("submit threw synchronously: " + (synchronous?.message || synchronous));
      setErr(String(synchronous?.message || synchronous || "Unexpected error"));
    } finally {
      setBusy(false);
      crumb("submit done");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-ink">
        <img
          src="https://images.pexels.com/photos/15564127/pexels-photo-15564127.jpeg"
          alt="Yacht at sea"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-ink/80 via-ink/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-white text-ink grid place-items-center">
              <Anchor className="h-4 w-4" />
            </div>
            <span className="font-display text-[17px] font-medium">Seavanto</span>
          </Link>
          <div>
            <div className="font-display text-3xl leading-tight max-w-md">
              "We replaced clipboards, WhatsApp groups and three Excel sheets in one weekend."
            </div>
            <div className="mt-4 text-sm text-white/70">Captain S. — 32m motor yacht, Palma</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
              <Anchor className="h-4 w-4" />
            </div>
            <span className="font-display text-[17px] font-medium">Seavanto</span>
          </div>
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight">{t("auth.welcome_back")}</h1>
            <p className="text-sm text-zinc-600 mt-1">{t("auth.sign_in_to_yacht")}</p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-zinc-600 hover:text-ink" data-testid="link-forgot">
                  {t("auth.forgot_password")}
                </Link>
              </div>
            </div>
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2"
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            onClick={(e) => {
              // Belt-and-suspenders: some iPad Safari versions (and some
              // PWA wrappers) occasionally swallow `submit` events triggered
              // by tapping a `type="submit"` button. Wiring `onClick` here
              // as a redundant entry point guarantees the handler runs.
              // submit() is idempotent: it bails out early if `busy` is
              // already true, so we never double-fire the API.
              if (e && typeof e.preventDefault === "function") e.preventDefault();
              submit(e);
            }}
            className="w-full bg-ink hover:bg-black"
            data-testid="login-submit"
          >
            {busy ? t("auth.signing_in") : t("auth.sign_in")} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          <div className="flex items-center justify-center pt-1">
            <LanguageSwitcher variant="full" testid="login-lang-switcher" />
          </div>

          <div className="text-sm text-zinc-600 text-center" data-testid="login-beta-notice">
            {t("auth.private_beta_notice")}{" "}
            <Link
              to="/request-access"
              className="text-ink underline"
              data-testid="link-request-access"
            >
              {t("auth.request_access")}
            </Link>
            .
          </div>
          <div className="text-sm text-zinc-600 text-center" data-testid="login-supplier-apply">
            Are you a marine supplier?{" "}
            <Link to="/supplier/apply" className="text-ink underline" data-testid="link-supplier-apply">
              Apply to join the marketplace
            </Link>
            .
          </div>
          {/* Build stamp + debug log opener. Tiny, low-contrast — visible
              only to people who scroll to the very bottom of the form.
              Critical for diagnosing "iPad shows old broken bundle" type
              issues, where the user's device is loading a stale chunk
              from CDN/Safari cache while desktop sees the new one. */}
          <div className="text-[10px] text-zinc-400 text-center font-mono" data-testid="login-build-stamp">
            build {process.env.REACT_APP_BUILD_ID || "dev"}
            {" · api "}
            {(() => {
              try { return new URL(API).host; } catch { return "(invalid)"; }
            })()}
            {" · "}
            <button
              type="button"
              className="underline"
              onClick={(e) => {
                e.preventDefault();
                try {
                  const dump = window.__seavantoDebug ? window.__seavantoDebug.dump() : null;
                  // Toggle the overlay by emitting a synthetic error so the
                  // user can see the breadcrumbs they accumulated tapping
                  // Sign In. Falls back to an inline alert if the overlay
                  // helper isn't available.
                  if (dump) {
                    window.dispatchEvent(new ErrorEvent("error", {
                      message: "diagnostic dump (manual):\n" + JSON.stringify(dump, null, 2),
                    }));
                  } else {
                    alert("Debug helper not loaded.");
                  }
                } catch (err) {
                  alert("Debug dump failed: " + (err && err.message ? err.message : err));
                }
              }}
              data-testid="login-debug-button"
            >
              show debug
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
